import React from 'react';
import { useAnimatedCounter } from '../../hooks/useAnimatedCounter';

interface AnimatedNumberProps {
  value: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Animated number display component with smooth counting effect
 * 
 * Usage:
 * <AnimatedNumber value={1500} prefix="$" decimals={2} />
 * <AnimatedNumber value={userShares} suffix=" shares" />
 */
export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 800,
  decimals = 0,
  prefix = '',
  suffix = '',
  className = '',
  style = {}
}) => {
  const { formattedValue, isAnimating } = useAnimatedCounter(value, {
    duration,
    decimals,
    prefix,
    suffix,
    easing: 'easeOut'
  });

  return (
    <span 
      className={`animated-number ${isAnimating ? 'counting' : ''} ${className}`}
      style={style}
    >
      {formattedValue}
    </span>
  );
};

/**
 * Animated currency display (USD by default)
 */
export const AnimatedCurrency: React.FC<Omit<AnimatedNumberProps, 'prefix' | 'decimals'> & { 
  currency?: '$' | '€' | '£' | 'R';
  showDecimals?: boolean;
}> = ({
  value,
  currency = '$',
  showDecimals = false,
  ...props
}) => {
  return (
    <AnimatedNumber
      value={value}
      prefix={currency}
      decimals={showDecimals ? 2 : 0}
      {...props}
    />
  );
};

/**
 * Animated percentage display
 */
export const AnimatedPercentage: React.FC<Omit<AnimatedNumberProps, 'suffix'> & {
  showDecimals?: boolean;
}> = ({
  value,
  showDecimals = false,
  ...props
}) => {
  return (
    <AnimatedNumber
      value={value}
      suffix="%"
      decimals={showDecimals ? 1 : 0}
      {...props}
    />
  );
};

export default AnimatedNumber;

